/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import { CircularProgress } from "@mui/material";
import * as React from "react";

import { useAuth } from "@/providers";
import { UserRecord } from "@/shared/domain/users";
import { useLazyAPICall } from "@/shared/services/apiHooks";
import { APIResponse, getUserEndpoint } from "@/types";

import { UserProfile } from "../shared/components/user/UserProfile/UserProfile";

interface ProfileProps {
  userId: string | undefined;
}

export const Profile: React.FC<ProfileProps> = ({ userId }) => {
  const { currentOrganization } = useAuth();
  const [fetchedUser, setFetchedUser] = React.useState<UserRecord>();
  const { initiateRequest, loading, error } =
    useLazyAPICall<APIResponse<UserRecord>>();

  React.useEffect(() => {
    async function fetchUser() {
      if (!userId || !currentOrganization) {
        return;
      }

      const { data } = await initiateRequest({
        endpoint: getUserEndpoint,
        method: "GET",
        orgId: currentOrganization.org_id,
        pathParams: {
          userId: userId,
        },
      });

      setFetchedUser(data?.data);
    }

    void fetchUser();
  }, [currentOrganization, userId, initiateRequest]);

  if (loading) {
    return <CircularProgress />;
  }

  if (error || fetchedUser === undefined) {
    return <div>Failed to load user</div>;
  }

  return <UserProfile user={fetchedUser} />;
};
